/**
 * Onglet « Aide MCP » de la page Compte (207:3148) — une planche (`CELLS_PLANCHE`/
 * `CELL_PLANCHE`, planche B de `@exidis/ui` 1.5.0 : cellules fermées, gouttière de 16 px du
 * paquet), pas un panneau unique qui empilerait des sections.
 *
 * Réécrit pour la conformité au standard MCP (MCP-U1..U4, § mcp/README.md) : plus aucun extrait
 * qui affiche un jeton ou un en-tête `Authorization: Bearer …` pour un humain, plus de passerelle
 * `mcp-remote` — l'agent se connecte désormais par OAuth (compte CIAM Exidis), à trois endroits
 * indépendants : le connecteur d'organisation claude.ai (ajouté une fois par l'Owner), la
 * commande `claude mcp add` (Claude Code), et `.vscode/mcp.json` (VS Code). Les gabarits viennent
 * de `lib/mcp-snippets.js`, seule source.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MCP_CLI_CLIENT_ID, MCP_URL } from '../lib/api.js';
import { buildClaudeCodeSnippet, buildVsCodeMcpJson } from '../lib/mcp-snippets.js';
import { Eyebrow } from './common.jsx';
import { CopyButton } from './CopyButton.jsx';
import { McpCell as Cell, SnippetBlock } from './McpParts.jsx';
import { CELLS_PLANCHE, TD, TH } from '../ui/theme.js';

const TROUBLESHOOTING_ROWS = ['401', '403', '429', '503'];

export function McpHelpPanel() {
  const { t } = useTranslation();

  const hosted = Boolean(MCP_URL);
  const healthzUrl = hosted ? `${MCP_URL.replace(/\/mcp$/, '')}/healthz` : null;
  const hasClientId = Boolean(MCP_CLI_CLIENT_ID);

  const claudeCodeSnippet =
    hosted && hasClientId
      ? buildClaudeCodeSnippet({ url: MCP_URL, clientId: MCP_CLI_CLIENT_ID })
      : null;
  const vsCodeSnippet = hosted ? buildVsCodeMcpJson({ url: MCP_URL }) : null;

  return (
    <div>
      <Eyebrow className="mb-3">{t('mcpHelp.eyebrow')}</Eyebrow>
      <p className="mb-2 max-w-prose text-sm text-ink-soft">{t('mcpHelp.intro')}</p>
      <p className="mb-4 max-w-prose text-sm text-ink-soft">
        {t('mcpHelp.publicPagePrefix')}{' '}
        <Link to="/aide-mcp" className="underline">
          {t('mcpHelp.publicPageLink')}
        </Link>
      </p>

      {!hosted ? (
        <p role="status" className="text-sm text-ink">
          {t('mcpHelp.noHostedUrl')}
        </p>
      ) : (
        <div className={`${CELLS_PLANCHE} grid-cols-1 md:grid-cols-6`}>
          {/* claude.ai / Claude Desktop — connecteur d'organisation, aucun secret côté client. */}
          <Cell span="md:col-span-6">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.claudeAiTitle')}</p>
            <p className="mt-2 text-sm text-ink-soft">{t('mcpHelp.claudeAiBody')}</p>
          </Cell>

          {/* Claude Code */}
          <Cell span="md:col-span-3">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.claudeCodeTitle')}</p>
            <p className="mt-2 text-xs text-ink-faint">{t('mcpHelp.claudeCodeBody')}</p>
            {claudeCodeSnippet ? (
              <SnippetBlock value={claudeCodeSnippet} />
            ) : (
              <p className="mt-2 text-xs text-ink-faint">
                {t('mcpHelp.claudeCodeMissingClientId')}
              </p>
            )}
          </Cell>

          {/* VS Code */}
          <Cell span="md:col-span-3">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.vscodeTitle')}</p>
            <p className="mt-2 text-xs text-ink-faint">{t('mcpHelp.vscodeBody')}</p>
            <SnippetBlock value={vsCodeSnippet} />
          </Cell>

          {/* Outils */}
          <Cell span="md:col-span-6">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.toolsTableTitle')}</p>
            <table className="mt-2 w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t('mcpHelp.toolsTableModeHeader')}</th>
                  <th className={TH}>{t('mcpHelp.toolsTableNamesHeader')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={TD}>{t('mcpHelp.modeLecture')}</td>
                  <td className={TD}>
                    <code className="font-mono text-[11px] text-ink-soft">
                      {t('mcpHelp.toolsRead')}
                    </code>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>{t('mcpHelp.modeEcriture')}</td>
                  <td className={TD}>
                    <code className="font-mono text-[11px] text-ink-soft">
                      {t('mcpHelp.toolsWrite')}
                    </code>
                  </td>
                </tr>
              </tbody>
            </table>
          </Cell>

          {/* Vérifier */}
          <Cell span="md:col-span-3">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.verificationTitle')}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="break-all border-2 border-line bg-paper px-2 py-1 font-mono text-xs text-ink">
                GET {healthzUrl}
              </code>
              <CopyButton value={healthzUrl} />
            </div>
            <p className="mt-2 text-xs text-ink-faint">{t('mcpHelp.verificationIntro')}</p>
            <p className="mt-1 text-xs text-ink-faint">{t('mcpHelp.verification401Normal')}</p>
            <p className="mt-2 text-xs text-ink-faint">{t('mcpHelp.verificationHint')}</p>
          </Cell>

          {/* Agent autonome (repli clé API) */}
          <Cell span="md:col-span-3">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.agentAutonomeTitle')}</p>
            <p className="mt-2 text-sm text-ink-soft">{t('mcpHelp.agentAutonomeBody')}</p>
          </Cell>

          {/* Prompt à donner à l'agent */}
          <Cell span="md:col-span-6">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.promptTitle')}</p>
            <pre className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-all border-2 border-line bg-paper p-3 font-mono text-xs text-ink">
              {t('mcpHelp.prompt')}
            </pre>
            <CopyButton value={t('mcpHelp.prompt')} className="mt-2" />
          </Cell>

          {/* Dépannage */}
          <Cell span="md:col-span-6">
            <p className="text-sm font-semibold text-ink">{t('mcpHelp.troubleshootingTitle')}</p>
            <table className="mt-2 w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t('mcpHelp.troubleshootingSymptomHeader')}</th>
                  <th className={TH}>{t('mcpHelp.troubleshootingCauseHeader')}</th>
                  <th className={TH}>{t('mcpHelp.troubleshootingActionHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {TROUBLESHOOTING_ROWS.map((row) => (
                  <tr key={row}>
                    <td className={TD}>{t(`mcpHelp.troubleshooting.${row}.symptom`)}</td>
                    <td className={TD}>{t(`mcpHelp.troubleshooting.${row}.cause`)}</td>
                    <td className={TD}>{t(`mcpHelp.troubleshooting.${row}.action`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Cell>
        </div>
      )}
    </div>
  );
}
